import { Skeleton } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router"
import { useLocation, useNavigate } from "react-router-dom"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { useLeadStatusChange } from "../../../hooks/lead/use-lead-status-change.tsx"
import { type RootState } from "../../../store.ts"
import { createContractFromProposal } from "../../Contracts/redux/contracts.thunks.ts"
import { ArchiveLeadModal } from "../../Leads/components/ArchiveLeadModal.tsx"
import {
  changeLeadStatus,
  fetchLeadsFilterOptions
} from "../../Leads/redux/leads.thunks.ts"
import { LeadLoadFailure } from "../components/LeadLoadFailure.tsx"
import { leadDisplayName } from "../helpers/lead-display-name.ts"
import { leadSetProp } from "../redux/lead.slice.ts"
import { fetchLead, updateLead } from "../redux/lead.thunks.ts"
import { type Lead, type LeadChecklistItem } from "../types/lead.types.ts"
import { LeadWorkspace } from "./components/LeadWorkspace.tsx"
import { type LeadWorkspacePatch } from "./lead-workspace-patch.ts"
import { useLeadMissingFields } from "./missing/use-lead-missing-fields.ts"

const LeadWorkspacePage = ({
  lead,
  fetching,
  notFound,
  saving,
  filterOptions,
  leadPermissions,
  getLeadById,
  createContractFromProposalById,
  updateLeadById,
  retryStageChange,
  setProp,
  getLeadsFilterOptions
}: ConnectedProps<typeof connector>) => {
  const id = useParams()?.id as string
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const [archiveOpen, setArchiveOpen] = useState(false)
  const { pendingStatus, applyRejectedStage, clearPendingStage } =
    useLeadMissingFields()

  const { confirmStatusChange } = useLeadStatusChange({
    refetchCallback: () => {
      if (id) getLeadById(id)
      // The transition the deep link was carrying has landed, so its marks have
      // nothing left to point at.
      clearPendingStage()
    },
    // A rejection answered while this lead is open rewrites the params the
    // marks derive from, so a retry that fails on a different field re-marks
    // the cards instead of leaving the first set standing.
    onMissingFields: ({ targetStatus, fields, leadId }) => {
      if (leadId && leadId !== id) return
      applyRejectedStage(targetStatus ?? null, fields ?? [])
    }
  })

  // The deep-linked stage is retried without a second confirmation: the user
  // already confirmed this move, and the rejection is what sent them here.
  const retryPendingStage = () => {
    if (pendingStatus != null) {
      retryStageChange({ id, target_status: pendingStatus })
    }
  }

  // The workspace must not render a lead another page left in the store: its
  // cards would offer to save the wrong record.
  const current = lead?.id === id ? lead : null

  useEffect(() => {
    if (id) {
      getLeadById(id)
    }
  }, [id])

  useEffect(() => {
    generateDocumentTitle(
      current
        ? leadDisplayName(current, t("leadWorkspace.header.untitled"))
        : "Lead Workspace"
    )
  }, [current, t])

  useEffect(() => {
    getLeadsFilterOptions()
  }, [])

  const mutatedLead: Lead | null = useMemo(() => {
    if (!current) return null
    if (!current?.source) return current

    return {
      ...current,
      source: filterOptions?.source?.find((s) => s?.value === current?.source)
        ?.label
    }
  }, [current, filterOptions?.source])

  if (fetching && !mutatedLead)
    return <Skeleton.Node active className={"size-full"} />

  if (!mutatedLead) {
    return (
      <LeadLoadFailure
        notFound={notFound}
        retrying={fetching}
        onRetry={() => getLeadById(id)}
      />
    )
  }

  const handleLeadSelect = (id: string, action: string) => {
    switch (action) {
      case "archive":
        return setArchiveOpen(true)
      case "back_to_list": {
        // `location.key` is react-router's own per-entry id; it is the
        // literal string "default" only for the entry the app was loaded on.
        // `window.history.length` was wrong for this: it counts the whole tab
        // session, including pages from before the SPA, so a lead opened by
        // pasting its URL into a tab that had another site loaded sent Back
        // off to that other site.
        if (location.key !== "default") {
          return navigate(-1)
        } else {
          return navigate(RoutesPaths.lead.list)
        }
      }
      default:
        return alert(`${action} - ${id}`)
    }
  }

  const handleLeadChecklistUpdate = async (
    lead_checklist: LeadChecklistItem[]
  ) => {
    try {
      await updateLeadById({ id, lead_checklist }).unwrap()
      setProp({ key: "saveResult", value: null })
    } catch {
      // The thunk already surfaced the failure; the checklist keeps the lead's
      // stored items rather than rejecting into an unhandled promise.
    }
  }

  const handleCreateContractFromProposal = async (proposalId: string) => {
    await createContractFromProposalById({
      lead_id: id,
      proposal_id: proposalId
    })
  }

  const handleStageChange = (targetStatus: number) =>
    confirmStatusChange({ id: mutatedLead.id }, targetStatus)

  const handleLeadPatch = async (patch: LeadWorkspacePatch) => {
    try {
      await updateLeadById({ id, ...patch }).unwrap()
      setProp({ key: "saveResult", value: null })
      retryPendingStage()
      return true
    } catch {
      return false
    }
  }

  return (
    <>
      <LeadWorkspace
        lead={mutatedLead}
        canEdit={!!leadPermissions?.leads?.lead_edit}
        canAssignOwner={!!leadPermissions?.features?.lead_assign_sales_manager}
        saving={saving}
        filterOptions={filterOptions}
        leadPermissions={leadPermissions}
        onLeadSelect={handleLeadSelect}
        onStageChange={handleStageChange}
        onStageRetry={retryPendingStage}
        onPatch={handleLeadPatch}
        onChecklistUpdate={handleLeadChecklistUpdate}
        onCreateContractFromProposal={handleCreateContractFromProposal}
      />
      <ArchiveLeadModal
        open={archiveOpen}
        leadId={mutatedLead.id}
        leadName={mutatedLead.lead_name}
        onCancel={() => setArchiveOpen(false)}
        onArchived={() => {
          setArchiveOpen(false)
          getLeadById(mutatedLead.id)
        }}
      />
    </>
  )
}

const mapStateToProps = (state: RootState) => ({
  fetching: state.lead.fetching || state.leads.fetchingFilterOptions,
  notFound: state.lead.notFound,
  saving: state.lead.saveFetching,
  lead: state.lead.lead,
  filterOptions: state.leads.filterOptions,
  leadPermissions: state.auth?.userInfo?.permissions?.codes?.leads
})
const mapDispatchToProps = {
  getLeadsFilterOptions: fetchLeadsFilterOptions,
  getLeadById: fetchLead,
  createContractFromProposalById: createContractFromProposal,
  updateLeadById: updateLead,
  retryStageChange: changeLeadStatus,
  setProp: leadSetProp
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(LeadWorkspacePage)
